// 🔧 voting_engine_registry.js
// ✅ Purpose: Central registry for all Let's Go Vote voting engines
// ✅ Features:
// - Defines supported voting engines
// - Centralizes labels and descriptions
// - Defines voting scope behavior
// - Defines result calculation strategy
// - Defines whether Best in Show applies
// - Defines whether votes are category-based or entry-based
// - Helps vote.js, ballot.js, leaderboard.js, audit.js, and settings pages avoid hard-coded logic
// - Fully block-commented for clarity and future devs

export const VOTING_ENGINE_REGISTRY = {
  standard: {
    key: "standard",
    label: "People’s Choice Voting",
    legacyLabel: "Standard Voting",
    description:
      "Voters select one entry per category. Winners are based on the highest number of votes.",
    managerDescription:
      "Voters select one entry per category. Winners are based on the highest number of votes.",
    voteScope: "category",
    resultStrategy: "count_votes",
    allowBestInShow: true,
    allowVoteUpdate: false,
    usesCategories: true,
    usesRankings: false,
    usesScoringFactors: false,
    usesPointTotals: false,
    family: "selection",
  },

  ranked_choice: {
    key: "ranked_choice",
    label: "Ranked Choice Voting",
    description:
      "Voters rank entries in order of preference. Winners are calculated through ranked-choice elimination rounds.",
    managerDescription:
      "Voters rank entries in order of preference. Winners are calculated through ranked-choice elimination rounds.",
    voteScope: "category",
    resultStrategy: "ranked_elimination",
    allowBestInShow: false,
    allowVoteUpdate: true,
    usesCategories: true,
    usesRankings: true,
    usesScoringFactors: false,
    usesPointTotals: false,
    family: "selection",
  },

  points: {
    key: "points",
    label: "Judged Points Voting",
    legacyLabel: "Points Voting",
    description:
      "Judges score each entry against configured scoring factors. Final results are based on average total score.",
    managerDescription:
      "Judges score each entry against configured scoring factors. Final results are based on average total score.",
    voteScope: "entry",
    resultStrategy: "average_score",
    allowBestInShow: false,
    allowVoteUpdate: true,
    usesCategories: false,
    usesRankings: false,
    usesScoringFactors: true,
    usesPointTotals: true,
    family: "judged",
    supportsBestInShow: false,
    supportsRankings: false,
    supportsScoringFactors: true,
    supportsScorecard: true,   
  },

  scorecard: {
    key: "scorecard",
    label: "Detailed Scorecard Voting",
    legacyLabel: "Scorecard Voting",
    description:
      "Judges complete a detailed scorecard for each entry. Final results are based on averaged scorecard results.",
    managerDescription:
      "Judges complete a detailed scorecard for each entry. Final results are based on averaged scorecard results.",
    voteScope: "entry",
    resultStrategy: "average_scorecard",
    allowBestInShow: false,
    allowVoteUpdate: true,
    usesCategories: false,
    usesRankings: false,
    usesScoringFactors: true,
    usesPointTotals: true,
    family: "judged",
  },
};

export const VOTING_FAMILIES = {
  selection: {
    key: "selection",
    label: "Selection-Based Voting",
  },

  judged: {
    key: "judged",
    label: "Judged Voting",
  },
};

// ✅ Judging mode keys used by Judged Points Voting
// ✅ "simple" = judge gives one score per scoring factor
// ✅ "scorecard" = judge gives individual criteria scores under each scoring factor
export const JUDGING_MODES = {
  SIMPLE: "simple",
  SCORECARD: "scorecard",
};

// ✅ Human-readable judging mode labels
export const JUDGING_MODE_LABELS = {
  [JUDGING_MODES.SIMPLE]: "Simple Points",
  [JUDGING_MODES.SCORECARD]: "Detailed Scorecard",
};

// ✅ Returns true when Judged Points is using the simple points form
export const isSimpleJudgingMode = (judgingMode) => {
  return !judgingMode || judgingMode === JUDGING_MODES.SIMPLE;
};

// ✅ Returns true when Judged Points is using the detailed scorecard form
export const isScorecardJudgingMode = (judgingMode) => {
  return judgingMode === JUDGING_MODES.SCORECARD;
};

// ✅ Gets the readable label for a judging mode
export const getJudgingModeLabel = (judgingMode) => {
  return JUDGING_MODE_LABELS[judgingMode] || JUDGING_MODE_LABELS[JUDGING_MODES.SIMPLE];
};

export function getVotingEngine(votingType = "standard") {
  return VOTING_ENGINE_REGISTRY[votingType] || VOTING_ENGINE_REGISTRY.standard;
}

export function getVotingTypeLabel(votingType = "standard") {
  return getVotingEngine(votingType).label;
}

export function getVotingTypeDescription(votingType = "standard") {
  return getVotingEngine(votingType).description;
}

export function getVotingTypeManagerDescription(votingType = "standard") {
  return getVotingEngine(votingType).managerDescription;
}

export function getVotingScope(votingType = "standard") {
  return getVotingEngine(votingType).voteScope;
}

export function getResultStrategy(votingType = "standard") {
  return getVotingEngine(votingType).resultStrategy;
}

export function allowsBestInShow(votingType = "standard") {
  return getVotingEngine(votingType).allowBestInShow === true;
}

export function allowsVoteUpdate(votingType = "standard") {
  return getVotingEngine(votingType).allowVoteUpdate === true;
}

export function usesScoringFactors(votingType = "standard") {
  return getVotingEngine(votingType).usesScoringFactors === true;
}

export function usesRankings(votingType = "standard") {
  return getVotingEngine(votingType).usesRankings === true;
}

export function usesPointTotals(votingType = "standard") {
  return getVotingEngine(votingType).usesPointTotals === true;
}

export function getVotingFamily(votingType = "standard") {
  return getVotingEngine(votingType).family;
}

export function getVotingFamilyLabel(votingType = "standard") {
  const familyKey = getVotingFamily(votingType);
  return VOTING_FAMILIES[familyKey]?.label || "Voting";
}

export function isSelectionVotingFamily(votingType = "standard") {
  return getVotingFamily(votingType) === "selection";
}

export function isJudgedVotingFamily(votingType = "standard") {
  return getVotingFamily(votingType) === "judged";
}

export function isCategoryScopedVoting(votingType = "standard") {
  return getVotingScope(votingType) === "category";
}

export function isEntryScopedVoting(votingType = "standard") {
  return getVotingScope(votingType) === "entry";
}